// Aurora Innovation, Inc. Proprietary and Confidential. Copyright 2026.
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import { buttonGroupClasses } from '@mui/material/ButtonGroup';
import { iconButtonClasses } from '@mui/material/IconButton';
import { isDarkMode } from '../../provider/types';
import { rem } from '../../tokens/conversions';
import { grey } from '../../tokens/primitiveColors/grey';
import { border } from '../../tokens/themeColors/border';
var iconButtonInGroup = "& > .".concat(iconButtonClasses.root);
export var ButtonGroup = function(mode) {
    var bd = border(mode);
    var containedBorder = isDarkMode(mode) ? grey[700] : grey[950];
    var outlinedBorder = isDarkMode(mode) ? grey[600] : bd.default;
    var textBorder = isDarkMode(mode) ? grey[750] : bd.soft;
    var _obj;
    var override = {
        MuiButtonGroup: {
            styleOverrides: {
                root: function(param) {
                    var ownerState = param.ownerState;
                    return _object_spread(// IconButton children: border-radius and variant-specific dividers
                    _define_property({
                        borderRadius: rem(8),
                        boxShadow: 'none'
                    }, iconButtonInGroup, _object_spread({
                        borderRadius: rem(0),
                        minWidth: 0,
                        '&:first-of-type': {
                            borderTopLeftRadius: rem(8),
                            borderBottomLeftRadius: rem(8)
                        },
                        '&:last-of-type': {
                            borderTopRightRadius: rem(8),
                            borderBottomRightRadius: rem(8)
                        },
                        // See the `grouped` slot's `:active` comment below.
                        '&:active, &.pseudo-active': {
                            zIndex: 1
                        }
                    }, ownerState.variant === 'contained' && {
                        '&:not(:last-of-type)': {
                            borderRight: "".concat(rem(1), " solid ").concat(containedBorder)
                        }
                    }, ownerState.variant === 'outlined' && {
                        border: "".concat(rem(1), " solid ").concat(outlinedBorder),
                        '&:not(:last-of-type)': {
                            borderRight: "".concat(rem(1), " solid ").concat(outlinedBorder)
                        },
                        '&:not(:first-of-type)': {
                            marginLeft: rem(-1)
                        }
                    }, ownerState.variant === 'text' && {
                        '&:not(:last-of-type)': {
                            borderRight: "".concat(rem(1), " solid ").concat(textBorder)
                        }
                    })), ownerState.variant === 'outlined' && _define_property({}, "& .".concat(buttonGroupClasses.firstButton, ":hover:not(:active), & .").concat(buttonGroupClasses.middleButton, ":hover:not(:active)"), ownerState.orientation === 'vertical' ? {
                        borderBottomColor: 'transparent'
                    } : {
                        borderRightColor: 'transparent'
                    }), ownerState.orientation === 'vertical' && _define_property({}, iconButtonInGroup, _object_spread({
                        borderRadius: rem(0),
                        minWidth: 0,
                        // Only set this selector's own rounded corners — the opposite
                        // corners are zeroed by the `:not(:first/last-of-type)` rules below.
                        // A single-button group matches both selectors at once, and setting
                        // all four corners in each (including explicit zeros) here would let
                        // whichever rule comes later in source order win outright, leaving
                        // the button with only two rounded corners instead of all four.
                        '&:first-of-type': {
                            borderTopLeftRadius: rem(8),
                            borderTopRightRadius: rem(8)
                        },
                        '&:last-of-type': {
                            borderBottomLeftRadius: rem(8),
                            borderBottomRightRadius: rem(8)
                        },
                        // See the `grouped` slot's `:active` comment below.
                        '&:active, &.pseudo-active': {
                            zIndex: 1
                        },
                        '&:not(:first-of-type)': {
                            borderTopLeftRadius: rem(0),
                            borderTopRightRadius: rem(0)
                        },
                        '&:not(:last-of-type)': {
                            borderBottomLeftRadius: rem(0),
                            borderBottomRightRadius: rem(0)
                        }
                    }, ownerState.variant === 'contained' && {
                        '&:not(:last-of-type)': {
                            borderRight: 'none',
                            borderBottom: "".concat(rem(1), " solid ").concat(containedBorder),
                            borderBottomLeftRadius: rem(0),
                            borderBottomRightRadius: rem(0)
                        }
                    }, ownerState.variant === 'outlined' && {
                        border: "".concat(rem(1), " solid ").concat(outlinedBorder),
                        '&:not(:last-of-type)': {
                            borderBottom: "".concat(rem(1), " solid ").concat(outlinedBorder),
                            borderBottomLeftRadius: rem(0),
                            borderBottomRightRadius: rem(0)
                        },
                        '&:not(:first-of-type)': {
                            marginTop: rem(-1),
                            marginLeft: 0,
                            borderTopLeftRadius: rem(0),
                            borderTopRightRadius: rem(0)
                        }
                    }, ownerState.variant === 'text' && {
                        '&:not(:last-of-type)': {
                            borderRight: 'none',
                            borderBottom: "".concat(rem(1), " solid ").concat(textBorder),
                            borderBottomLeftRadius: rem(0),
                            borderBottomRightRadius: rem(0)
                        }
                    })));
                },
                grouped: {
                    minWidth: 0,
                    '&:not(:first-of-type)': {
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0
                    },
                    '&:not(:last-of-type)': {
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0
                    },
                    // Outlined buttons overlap their next sibling by -1px (see
                    // groupedOutlined/vertical below) so the divider is a single crisp
                    // line. Without this, the later sibling always paints over that 1px
                    // seam — including a pressed button's highlighted border, which ends
                    // up hidden behind its un-pressed neighbor. Raise the pressed button
                    // above its siblings so its full border actually shows.
                    '&:active, &.pseudo-active': {
                        zIndex: 1
                    }
                },
                groupedContained: {
                    border: 'none',
                    '&:not(:last-of-type)': {
                        borderRight: "".concat(rem(1), " solid ").concat(containedBorder)
                    }
                },
                groupedText: {
                    border: 'none',
                    '&:not(:last-of-type)': {
                        borderRight: "".concat(rem(1), " solid ").concat(textBorder)
                    }
                },
                vertical: (_obj = {}, _define_property(_obj, "& .".concat(buttonGroupClasses.grouped), {
                    // `grouped`'s own rule above zeroes left/right corners for
                    // horizontal layout (not-first loses its left corners, not-last
                    // loses its right corners) — wrong for vertical, where the first
                    // button should keep BOTH top corners and the last button should
                    // keep BOTH bottom corners. Restate all four corners explicitly
                    // here; this selector has higher specificity (two classes) than
                    // the plain `grouped` rule (one class) so it wins.
                    '&:first-of-type': {
                        borderTopLeftRadius: rem(8),
                        borderTopRightRadius: rem(8)
                    },
                    '&:last-of-type': {
                        borderBottomLeftRadius: rem(8),
                        borderBottomRightRadius: rem(8)
                    },
                    '&:not(:first-of-type)': {
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0
                    },
                    '&:not(:last-of-type)': {
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0
                    }
                }), _define_property(_obj, "& .".concat(buttonGroupClasses.groupedContained), {
                    '&:not(:last-of-type)': {
                        borderRight: 'none',
                        borderBottom: "".concat(rem(1), " solid ").concat(containedBorder)
                    }
                }), _define_property(_obj, "& .".concat(buttonGroupClasses.groupedText), {
                    '&:not(:last-of-type)': {
                        borderRight: 'none',
                        borderBottom: "".concat(rem(1), " solid ").concat(textBorder)
                    }
                }), _obj)
            },
            defaultProps: {
                disableElevation: true
            }
        }
    };
    return override;
};

//# sourceMappingURL=ButtonGroup.js.map